/**
 * Login — Figma `01/ Entry/ Login` (node 12:311), SignInCard 12:391.
 *
 * Measured: 400px card, `bg/surface` on the navy backdrop, `radius/lg` (16px),
 * pt-32/pb-28/px-32, 20px gap, the sign-in-card shadow (12:391, the file's only
 * shadow), the GS mark, "Sign in" in `H1/24`, the subtitle in `Body/13`
 * `text/secondary`, two `Input/Text`, the options row, `Button/Primary`, and the
 * © line in `Caption/12`.
 *
 * ===========================================================================
 * THE THREE REFUSALS THIS SCREEN MUST TELL APART
 * ===========================================================================
 * Figma has no error state for this card, so the states are authored — and they
 * are authored from the API's actual behaviour rather than from a generic
 * "something went wrong":
 *
 *  · `INVALID_CREDENTIALS` (401) — the API deliberately does not say whether the
 *    email or the password was wrong (`auth-errors.ts`: "enumeration-safe"). The
 *    UI must not pretend to know either, so ONE message covers both. Marking the
 *    email field invalid would leak an answer the API refused to give.
 *
 *  · `ACCOUNT_LOCKED` (423) — the lockout. `details.retry_after_seconds` is on the
 *    envelope, so the message says WHEN, which is the only actionable part. Note
 *    the API reuses this code for a DISABLED account (403); the two are
 *    distinguished by status, and a disabled account gets the message that
 *    matches it instead of a wait that will never end.
 *
 *  · transport failure — the API is not running. Told plainly, because during
 *    local development it is by far the most common cause and "invalid
 *    credentials" would send the developer hunting for the wrong bug.
 *
 * Validation is real and client-side first: a blank field or a malformed address
 * never becomes a request, because a 400 from the server is a worse way to learn
 * you typed nothing.
 *
 * "Remember me" and "Forgot password?" ARE IN THE DESIGN AND ARE NOT WIRED. Both
 * are rendered — removing designed controls would be a silent divergence — but
 * neither has an API: there is no reset endpoint in spec §10, and the refresh
 * token is already persisted for every login (D-web-1), so a "remember me" that
 * toggled nothing would be a lie. They are disabled with an explanation instead
 * of quietly doing nothing. Recorded in PHASE2-REPORT.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { ApiRequestError, ApiTransportError } from '../lib/api';
import { useSession } from './session';
import { EntryBackdrop, G1Mark } from '../components/EntryBackdrop';
import { Alert, Button, Checkbox, TextField } from '../components/ui';

/** Deliberately permissive: the server is the authority on a valid address. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FieldErrors {
  email: string | null;
  password: string | null;
}

function formMessage(error: unknown): { title: string; body: string } {
  if (error instanceof ApiTransportError) {
    return {
      title: 'Cannot reach the server',
      body: 'The Global One API did not respond. Check that it is running, then try again.',
    };
  }
  if (error instanceof ApiRequestError) {
    if (error.code === 'ACCOUNT_LOCKED') {
      // 423 = inside the lockout window; 403 = the account is disabled. Same code,
      // different remediation (`apps/api/src/modules/auth/auth-errors.ts`).
      if (error.status === 403) {
        return {
          title: 'This account is disabled',
          body: 'Ask your administrator to re-enable it.',
        };
      }
      const seconds = retryAfterSeconds(error.details);
      return {
        title: 'Too many failed sign-in attempts',
        body:
          seconds === null
            ? 'This account is locked for a short while. Try again later.'
            : `This account is locked. Try again in ${describeWait(seconds)}.`,
      };
    }
    if (error.code === 'INVALID_CREDENTIALS') {
      return {
        title: 'Email or password is incorrect',
        body: 'Check both and try again.',
      };
    }
    return { title: 'Sign-in failed', body: error.message };
  }
  return {
    title: 'Sign-in failed',
    body: 'Something went wrong. Try again.',
  };
}

function retryAfterSeconds(details: unknown): number | null {
  if (typeof details !== 'object' || details === null) return null;
  const value = (details as { retry_after_seconds?: unknown }).retry_after_seconds;
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

function describeWait(seconds: number): string {
  if (seconds < 90) return `${String(Math.ceil(seconds))} seconds`;
  const minutes = Math.ceil(seconds / 60);
  return `${String(minutes)} minute${minutes === 1 ? '' : 's'}`;
}

export function LoginPage(): React.JSX.Element {
  const { status, signIn } = useSession();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [fields, setFields] = useState<FieldErrors>({ email: null, password: null });
  const [formError, setFormError] = useState<{ title: string; body: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/countries'} replace />;
  }

  const validate = (): boolean => {
    const next: FieldErrors = {
      email:
        email.trim() === ''
          ? 'Enter your email address.'
          : EMAIL_SHAPE.test(email.trim())
            ? null
            : 'That does not look like an email address.',
      password: password === '' ? 'Enter your password.' : null,
    };
    setFields(next);
    return next.email === null && next.password === null;
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setFormError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      // Navigation is handled by the `status === 'authenticated'` branch above once
      // the provider settles; nothing to push here.
    } catch (error) {
      setFormError(formMessage(error));
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <EntryBackdrop>
      <div className="mx-auto w-full max-w-[400px]">
        <form
          noValidate
          onSubmit={(event) => {
            void onSubmit(event);
          }}
          className="flex flex-col gap-5 rounded-lg border border-fg-inverse/50 bg-surface px-8 pb-7 pt-8 shadow-[var(--crf-shadow-signin-card)]"
        >
          <G1Mark />

          <div className="flex flex-col gap-1">
            <h1 className="text-h1 text-fg">Sign in</h1>
            <p className="text-body13 text-fg-secondary">Welcome back to Global One</p>
          </div>

          {formError === null ? null : (
            <Alert tone="danger" title={formError.title}>
              {formError.body}
            </Alert>
          )}

          <TextField
            label="Email"
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            placeholder="you@globalsoftwares.in"
            value={email}
            error={fields.email}
            disabled={submitting}
            onChange={(event) => {
              setEmail(event.target.value);
              if (fields.email !== null) setFields((prev) => ({ ...prev, email: null }));
            }}
          />

          <TextField
            label="Password"
            type={revealed ? 'text' : 'password'}
            name="password"
            autoComplete="current-password"
            placeholder="••••••••••"
            value={password}
            error={fields.password}
            disabled={submitting}
            onChange={(event) => {
              setPassword(event.target.value);
              if (fields.password !== null) setFields((prev) => ({ ...prev, password: null }));
            }}
            trailing={
              <button
                type="button"
                onClick={() => setRevealed((prev) => !prev)}
                aria-label={revealed ? 'Hide password' : 'Show password'}
                aria-pressed={revealed}
                className="shrink-0 text-fg-muted hover:text-fg-secondary"
              >
                {revealed ? (
                  <EyeOff aria-hidden="true" className="size-5" />
                ) : (
                  <Eye aria-hidden="true" className="size-5" />
                )}
              </button>
            }
          />

          <div className="flex items-center justify-between gap-3">
            {/* In the design, not wired — see the file header. */}
            <Checkbox
              label="Remember me"
              checked
              disabled
              readOnly
              title="Your session is always remembered on this device until you sign out."
            />
            <button
              type="button"
              disabled
              title="Password reset is not available yet — ask your administrator."
              className="text-body13 font-medium text-indigo disabled:cursor-not-allowed disabled:opacity-60"
            >
              Forgot password?
            </button>
          </div>

          <Button type="submit" block disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign In'}
          </Button>

          <p className="text-center text-label text-fg-secondary">© Global Software 2026</p>
        </form>
      </div>
    </EntryBackdrop>
  );
}
